import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
	FiArrowLeft,
	FiAward,
	FiBookOpen,
	FiCheck,
	FiChevronDown,
	FiClock,
	FiInfo,
	FiPlay,
	FiStar,
	FiTarget,
	FiUsers,
} from 'react-icons/fi';
import { FiLinkedin, FiTwitter, FiGithub, FiGlobe } from 'react-icons/fi';
import { useSettings } from '../contexts/SettingsContext';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import ScrollAnimation from '../components/ScrollAnimation';
import { courseService } from '../services/api';

// Layout wrappers
const Page = styled.div`
	width: 100%;
	overflow-x: hidden;
`;

const Container = styled.div`
	max-width: ${p => p.theme.breakpoints.wide};
	margin: 0 auto;
	padding: 0 ${p => p.theme.spacing.lg};

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding: 0 ${p => p.theme.spacing.md};
	}
`;

// Hero
const Hero = styled.section`
	background: ${p => p.theme.colors.gradient.primary};
	color: ${p => p.theme.colors.light};
	padding: ${p => p.theme.spacing['4xl']} 0 ${p => p.theme.spacing['2xl']};
	position: relative;

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding: ${p => p.theme.spacing['3xl']} 0 ${p => p.theme.spacing.xl};
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		padding: ${p => p.theme.spacing['2xl']} 0 ${p => p.theme.spacing.lg};
	}
`;

const BackLink = styled(Link)`
	display: inline-flex; align-items: center; gap: 8px;
	color: rgba(255,255,255,0.9); text-decoration: none; font-weight: 600;
	margin-bottom: ${p => p.theme.spacing.lg};
`;

const HeroGrid = styled.div`
	display: grid; grid-template-columns: 1.6fr 1.4fr; gap: ${p => p.theme.spacing['3xl']};
	@media (max-width: ${p => p.theme.breakpoints.desktop}) { grid-template-columns: 1fr; }
	@media (max-width: ${p => p.theme.breakpoints.tablet}) { gap: ${p => p.theme.spacing.xl}; }
`;

const HeroContent = styled.div`
	h1 { color: ${p => p.theme.colors.light}; margin: 0 0 ${p => p.theme.spacing.sm}; }
	.subtitle { opacity: 0.95; margin: ${p => p.theme.spacing.sm} 0 ${p => p.theme.spacing.lg}; line-height: 1.6; }
`;

const RatingRow = styled.div`
	display: inline-flex; align-items: center; gap: 10px; font-weight: 700;
	.stars { 
		color: #FFD700; 
		display: inline-flex; 
		gap: 4px;
		
		svg {
			&.filled {
				fill: #FFD700;
			}
			&.empty {
				fill: none;
			}
		}
	}
	margin-bottom: ${p => p.theme.spacing.sm};
`;

const Highlights = styled.ul`
	list-style: none; padding: 0; margin: ${p => p.theme.spacing.lg} 0;
	display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.xl};
	@media (max-width: ${p => p.theme.breakpoints.mobile}) { grid-template-columns: 1fr; }
	li { display: flex; align-items: center; gap: 10px; font-weight: 600; }
`;

const HeroImageBox = styled.div`
	border-radius: 24px; overflow: hidden; position: relative; background: rgba(255,255,255,0.25);
	padding: 10px; aspect-ratio: 16/9; max-height: 460px;

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		max-height: 380px;
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		border-radius: 18px; padding: 8px;
		/* Prevent the hero image from becoming excessively tall */
		max-height: 280px;
		margin-bottom: ${p => p.theme.spacing.lg};
	}
`;

const HeroImage = styled.img`
	width: 100%; height: 100%; object-fit: cover; object-position: center; border-radius: 16px; background:${p=>p.theme.colors.gray[50]};
`;

const CTAGroup = styled.div`
	display: flex; align-items: center; gap: ${p => p.theme.spacing.md}; flex-wrap: wrap;

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		flex-direction: column; align-items: stretch; gap: ${p => p.theme.spacing.sm};
	}
`;

const PrimaryCTA = styled(Button)`
	background: ${p => p.theme.colors.gradient.primary};
	color: white;
	border: none;
	font-weight: 800;
	padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.xl};
	box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
	transition: all 0.3s ease;
	position: relative;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: -100%;
		width: 100%;
		height: 100%;
		background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
		transition: left 0.5s;
	}

	&:hover {
		background: ${p => p.theme.colors.gradient.secondary};
		transform: translateY(-3px);
		box-shadow: 0 12px 32px rgba(239, 68, 68, 0.5);
	}

	&:hover::before {
		left: 100%;
	}

	&:active {
		transform: translateY(-1px);
		box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		width: 100%;
		text-align: center;
		padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
	}
`;

const OutlineCTA = styled(Button)`
	background: transparent;
	color: ${p => p.theme.colors.light};
	border: 2px solid ${p => p.theme.colors.light};
	font-weight: 700;
	padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.lg};
	transition: all 0.3s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.15);
		border-color: ${p => p.theme.colors.light};
		transform: translateY(-2px);
		box-shadow: ${p => p.theme.shadows.md};
	}

	&:active {
		transform: translateY(0);
		background: rgba(255, 255, 255, 0.1);
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		width: 100%;
		text-align: center;
		padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
	}
`;

// Tabs
const TabsWrap = styled.div`
	position: absolute;
	left: 0; right: 0; bottom: -28px;
	display: flex; justify-content: center;
`;

const TabList = styled.div`
	display: inline-flex; gap: ${p => p.theme.spacing.sm};
	background: ${p => p.theme.colors.light};
	padding: 6px;
	border-radius: 999px;
	box-shadow: ${p => p.theme.shadows.sm};
	border: 1px solid ${p => p.theme.colors.gray[200]};
	overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; &::-webkit-scrollbar{display:none}
`;

const Tab = styled.button`
	white-space: nowrap; border: 0; border-radius: 999px; padding: 10px 14px; font-weight: 700; cursor: pointer;
	background: ${p => p.active ? p.theme.colors.primary : 'transparent'};
	color: ${p => p.active ? p.theme.colors.light : p.theme.colors.gray[800]};
	box-shadow: none;
	transition: ${p => p.theme.transitions.default};
	display: inline-flex; align-items: center; gap: 8px;

	svg { opacity: ${p => p.active ? 1 : 0.7}; transition: ${p => p.theme.transitions.default}; }

	&:hover {
		background: ${p => p.active ? p.theme.colors.primary : p.theme.colors.gray[100]};
		color: ${p => p.active ? p.theme.colors.light : p.theme.colors.gray[900]};
	}

	&:focus-visible {
		outline: 2px solid ${p => p.theme.colors.gray[300]};
		outline-offset: 2px;
	}

		@media (max-width: ${p => p.theme.breakpoints.mobile}) {
			padding: 8px 12px;
		}
`;

// Sections and cards
const Section = styled.section`
	padding: ${p => p.theme.spacing['3xl']} 0;

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding: ${p => p.theme.spacing['2xl']} 0;
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		padding: ${p => p.theme.spacing.xl} 0;
	}
`;

// First content section below the tabs gets extra top padding to clear the overlap
const ContentSection = styled(Section)`
	padding-top: calc(${p => p.theme.spacing['3xl']} + 40px);

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding-top: calc(${p => p.theme.spacing['2xl']} + 32px);
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		padding-top: calc(${p => p.theme.spacing.xl} + 24px);
	}
`;

const SectionGrid = styled.div`
	display: grid; 
	grid-template-columns: 1.5fr 1fr; 
	gap: ${p => p.theme.spacing['2xl']};
	align-items: stretch;
	
	@media (max-width: ${p => p.theme.breakpoints.desktop}) { 
		grid-template-columns: 1fr; 
	}
`;

const Card = styled.div`
	background: ${p => p.theme.colors.light}; 
	border: 2px solid ${p => p.theme.colors.gray[200]}; 
	border-radius: ${p => p.theme.borderRadius.xl};
	padding: ${p => p.theme.spacing['2xl']}; 
	box-shadow: ${p => p.theme.shadows.sm};
	height: 100%;
	display: flex;
	flex-direction: column;
	
	h3{ 
		margin: 0 0 ${p => p.theme.spacing.md}; 
		flex-shrink: 0;
	}

	ul {
		flex-grow: 1;
	}

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding: ${p => p.theme.spacing.xl};
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		padding: ${p => p.theme.spacing.lg};
	}
`;

// Instructor styles
const InstructorHeader = styled.div`
	display: flex; align-items: center; gap: ${p => p.theme.spacing.lg};

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		flex-direction: column; text-align: center;
	}
`;

const Avatar = styled.img`
	width: 72px; height: 72px; border-radius: 50%; object-fit: cover;
	border: 2px solid ${p => p.theme.colors.gray[200]}; background: ${p => p.theme.colors.gray[100]};

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		width: 60px; height: 60px;
	}
`;

const AvatarFallback = styled.div`
	width: 72px; height: 72px; border-radius: 50%; display:flex; align-items:center; justify-content:center;
	font-weight: 800; color: ${p => p.theme.colors.gray[700]};
	background: ${p => p.theme.colors.gray[100]}; border: 2px solid ${p => p.theme.colors.gray[200]};

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		width: 60px; height: 60px;
	}
`;

const BadgeRow = styled.div`
	display:flex; flex-wrap:wrap; gap: 8px; margin-top: 8px;
`;

const Badge = styled.span`
	display:inline-flex; align-items:center; gap: 6px; padding: 6px 10px; border-radius: 999px;
	background:${p=>p.theme.colors.gray[100]}; color:${p=>p.theme.colors.gray[700]};
	border:1px solid ${p=>p.theme.colors.gray[200]}; font-size:${p=>p.theme.fontSizes.sm};
`;

const SocialLinks = styled.div`
	display:flex; gap: 12px; margin-top: ${p=>p.theme.spacing.sm};
	a{ color:${p=>p.theme.colors.primary}; }
`;

const TagCloud = styled.div`
	display: flex; flex-wrap: wrap; gap: ${p => p.theme.spacing.sm};
`;

const Tag = styled.span`
	display: inline-flex; align-items: center; padding: 8px 12px; border-radius: 999px; background: ${p => p.theme.colors.gray[100]};
	color: ${p => p.theme.colors.gray[700]}; font-weight: 600; font-size: ${p => p.theme.fontSizes.sm};
`;

const StatsRow = styled.div`
	margin-top: ${p => p.theme.spacing['2xl']}; display: grid; gap: ${p => p.theme.spacing.md};
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	.stat{ display:flex; align-items:center; gap:10px; padding:12px 14px; background:${p=>p.theme.colors.gray[50]}; border:1px solid ${p=>p.theme.colors.gray[200]}; border-radius:${p=>p.theme.borderRadius.lg}; color:${p=>p.theme.colors.gray[700]}; }
	.value{ font-weight: 800; color: ${p => p.theme.colors.dark}; }
`;

const Small = styled.small` color: ${p => p.theme.colors.gray[600]}; `;

const CurriculumList = styled.div`
	.module{ 
		background:${p=>p.theme.colors.gray[50]}; 
		border:2px solid ${p=>p.theme.colors.gray[200]}; 
		border-radius:${p=>p.theme.borderRadius.xl}; 
		margin-bottom:${p=>p.theme.spacing.lg}; 
		overflow:hidden;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}
	
	.module:hover {
		border-color: ${p=>p.theme.colors.primary}40;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	}
	
	.module-header{ 
		padding:${p=>p.theme.spacing.xl}; 
		background:${p=>p.theme.colors.light}; 
		display:flex; 
		justify-content:space-between; 
		align-items:center; 
		cursor:pointer;
		transition: background 0.2s ease;
		user-select: none;
	}
	
	.module-header:hover {
		background: ${p=>p.theme.colors.gray[50]};
	}
	
	.module-header h4 {
		margin: 0;
		transition: color 0.2s ease;
	}
	
	.module-header:hover h4 {
		color: ${p=>p.theme.colors.primary};
	}
	
	.module-header .meta{ 
		display:inline-flex; 
		align-items:center; 
		gap:8px; 
	}
	
	.module-header .count{ 
		display:flex; 
		flex-direction:column; 
		line-height:1; 
		text-align:right; 
		min-width:38px; 
	}
	
	.module-header .count .n{ 
		font-weight:800; 
		color:${p=>p.theme.colors.dark}; 
		font-size:${p=>p.theme.fontSizes.md}; 
	}
	
	.module-header .count .label{ 
		color:${p=>p.theme.colors.gray[600]}; 
		font-size:${p=>p.theme.fontSizes.xs}; 
	}
	
	.module-content{ 
		padding:${p=>p.theme.spacing.xl}; 
		background:${p=>p.theme.colors.light};
		animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		border-top: 1px solid ${p=>p.theme.colors.gray[200]};
	}

	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		.module { margin-bottom: ${p => p.theme.spacing.md}; border-radius: ${p => p.theme.borderRadius.lg}; }
		.module-header { padding: ${p => p.theme.spacing.lg}; }
		.module-header h4 { font-size: ${p => p.theme.fontSizes.lg}; margin: 0; }
		.module-header .count{ min-width:34px; }
		.module-content { padding: ${p => p.theme.spacing.lg}; }
		.module-content li { padding: 10px 0; }
	}
`;

function CourseDetail() {
	const { slug } = useParams();
	const { settings } = useSettings();
	const [loading, setLoading] = useState(true);
	const [course, setCourse] = useState(null);
	const [activeTab, setActiveTab] = useState('overview');
	const [expanded, setExpanded] = useState(new Set([0]));

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const c = await courseService.getCourseBySlug(slug);
				if (!c) { setCourse(null); return; }
				const csv = s => typeof s === 'string' ? s.split(/[;\n,]+/).map(x=>x.trim()).filter(Boolean) : Array.isArray(s) ? s : [];
				// Normalize instructor details from various possible shapes
				const instructorRaw = c.instructor || c.teacher || c.author || c?.attributes?.instructor;
				const instructor = (() => {
					if (instructorRaw && typeof instructorRaw === 'object') {
						return {
							name: instructorRaw.name || instructorRaw.fullName || instructorRaw.title || instructorRaw.username || '',
							role: instructorRaw.role || instructorRaw.position || instructorRaw.designation || 'Instructor',
							bio: instructorRaw.bio || instructorRaw.about || '',
							avatar: instructorRaw.avatar || instructorRaw.photo || instructorRaw.image || instructorRaw.picture || '',
							experience: instructorRaw.experience || instructorRaw.experienceYears || instructorRaw.years || instructorRaw.yearsOfExperience,
							rating: instructorRaw.rating || instructorRaw.avgRating || null,
							socials: {
								linkedin: instructorRaw.linkedin || instructorRaw.socials?.linkedin,
								twitter: instructorRaw.twitter || instructorRaw.socials?.twitter,
								github: instructorRaw.github || instructorRaw.socials?.github,
								website: instructorRaw.website || instructorRaw.socials?.website,
							}
						};
					}
					return {
						name: c.instructorName || c.instructor || c.teacherName || '',
						role: c.instructorTitle || c.instructorRole || 'Instructor',
						bio: c.instructorBio || '',
						avatar: c.instructorImage || c.instructorAvatar || '',
						experience: c.instructorExperience || c.experienceYears,
						rating: c.instructorRating || null,
						socials: {
							linkedin: c.instructorLinkedin,
							twitter: c.instructorTwitter,
							github: c.instructorGithub,
							website: c.instructorWebsite,
						}
					};
				})();

				setCourse({
					...c,
					skills: csv(c.skills),
					learningOutcomes: csv(c.learningOutcomes),
					highlights: csv(c.highlights),
					prerequisites: csv(c.prerequisites),
					certificate: c.certificate ?? c.hasCertificate ?? c.completionCertificate ?? null,
					instructor,
					curriculum: Array.isArray(c.curriculum) ? c.curriculum.map((m,i)=>(
						{
							module: m.module || i+1,
							title: m.title || m?.attributes?.title || '',
							lessons: Array.isArray(m.lessons) ? m.lessons : [],
						}
					)) : [],
				});
			} catch (e) {
				// eslint-disable-next-line no-console
				setCourse(null);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [slug]);

	if (loading) return <LoadingSpinner fullScreen text="Loading course details..." />;
	if (!course) return <Navigate to="/courses" replace />;

	const tabs = [
		{ id: 'overview', label: 'Overview', icon: FiInfo },
		{ id: 'courseinfo', label: 'Course info', icon: FiBookOpen },
		{ id: 'curriculum', label: 'Curriculum', icon: FiBookOpen, count: course.curriculum?.length || 0 },
	];

	const whatsappUrl = (() => {
		const raw = settings?.social?.whatsapp; if (!raw) return '';
		const number = String(raw).replace(/[^\d]/g, '');
		if (!number) return '';
		const text = course?.title ? `Hi, I would like to know about ${course.title} course pricing` : 'Hi, I would like to know about course pricing';
		return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
	})();

	const goToCurriculum = () => { setActiveTab('curriculum'); document.getElementById('course-content')?.scrollIntoView({ behavior: 'smooth' }); };

	const renderOverview = () => (
		<>
			<SectionGrid>
				{(course.learningOutcomes?.length > 0) && (
					<ScrollAnimation animation="fadeInUp">
						<Card>
							<h3>Learning Objectives</h3>
							<ul>
								{course.learningOutcomes.slice(0, 10).map((x, i) => <li key={i}>{x}</li>)}
							</ul>
						</Card>
					</ScrollAnimation>
				)}
				{(course.skills?.length > 0 || course.students || course.duration || course.newCourse || course.certificate) && (
					<ScrollAnimation animation="fadeInUp" delay={100}>
						<Card>
							{course.skills?.length > 0 && (
								<>
									<h3>Stack</h3>
									<TagCloud>
										{course.skills.slice(0, 20).map((s, i) => <Tag key={i}>{s}</Tag>)}
									</TagCloud>
								</>
							)}
							<StatsRow>
								{course.students && (
									<div className="stat"><FiUsers /><span className="value">{course.students}</span><Small>Total Enrolled</Small></div>
								)}
								{course.duration && (
									<div className="stat"><FiClock /><span className="value">{course.duration}</span><Small>Duration</Small></div>
								)}
								{course.newCourse && (
									<div className="stat"><FiTarget /><span className="value">New Course</span><Small>Status</Small></div>
								)}
								{(course.certificate === true || course.certificate === 'Yes' || course.certificate === 'yes') && (
									<div className="stat"><FiAward /><span className="value">Yes</span><Small>Completion Certificate</Small></div>
								)}
							</StatsRow>
						</Card>
					</ScrollAnimation>
				)}
			</SectionGrid>

			{(course.instructor?.name || course.instructor?.bio || course.instructor?.avatar) && (
				<ScrollAnimation animation="fadeInUp" delay={200}>
					<Card style={{ marginTop: '2rem' }}>
						<h3>Instructor</h3>
						<InstructorHeader>
							{course.instructor?.avatar ? (
								<Avatar src={course.instructor.avatar} alt={course.instructor.name || 'Instructor'} onError={(e)=>{ e.currentTarget.style.display='none'; }} />
							) : (
								<AvatarFallback>{(course.instructor?.name || 'I').slice(0,1).toUpperCase()}</AvatarFallback>
							)}
							<div>
								<h4 style={{ margin: 0 }}>{course.instructor?.name}</h4>
								{course.instructor?.role && <Small>{course.instructor.role}</Small>}
								<BadgeRow>
									{course.instructor?.experience && (<Badge><FiTarget/> {course.instructor.experience}+ yrs experience</Badge>)}
									{course.instructor?.rating && (<Badge><FiStar/> {course.instructor.rating} rating</Badge>)}
								</BadgeRow>
								{(course.instructor?.socials?.linkedin || course.instructor?.socials?.twitter || course.instructor?.socials?.github || course.instructor?.socials?.website) && (
									<SocialLinks>
										{course.instructor.socials.linkedin && <a href={course.instructor.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FiLinkedin/></a>}
										{course.instructor.socials.twitter && <a href={course.instructor.socials.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter"><FiTwitter/></a>}
										{course.instructor.socials.github && <a href={course.instructor.socials.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub"><FiGithub/></a>}
										{course.instructor.socials.website && <a href={course.instructor.socials.website} target="_blank" rel="noopener noreferrer" aria-label="Website"><FiGlobe/></a>}
									</SocialLinks>
								)}
							</div>
						</InstructorHeader>
						{course.instructor?.bio && (
							<p style={{ marginTop: '1rem' }}>{course.instructor.bio}</p>
						)}
					</Card>
				</ScrollAnimation>
			)}
		</>
	);

	const renderCourseInfo = () => (
		<div>
			{course.fullDescription && (
				<div style={{ marginBottom: 32 }}>
					<h3>Course Description</h3>
					<p style={{ lineHeight: 1.8, color: '#333', whiteSpace: 'pre-line' }}>{course.fullDescription}</p>
				</div>
			)}
			{course.learningOutcomes?.length > 0 && (
				<>
					<h3>What You'll Learn</h3>
					<ul style={{ listStyle: 'none', padding: 0 }}>
						{course.learningOutcomes.map((o, i) => (
							<li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}><FiCheck style={{ color: '#0d6efd', marginTop: 2 }} /> {o}</li>
						))}
					</ul>
				</>
			)}
			{course.prerequisites?.length > 0 && (
				<div style={{ marginTop: 24 }}>
					<h4>Prerequisites</h4>
					<ul>
						{course.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
					</ul>
				</div>
			)}
		</div>
	);

	const renderCurriculum = () => (
		<CurriculumList>
			{course.curriculum?.length ? course.curriculum.map((m, idx) => {
				const isOpen = expanded.has(idx);
				return (
						<div className="module" key={idx}>
							<div className="module-header" onClick={() => { const n = new Set(expanded); n.has(idx) ? n.delete(idx) : n.add(idx); setExpanded(n); }}>
								<h4>Module {m.module}: {m.title}</h4>
								<span className="meta">
									<span className="count"><span className="n">{m.lessons?.length || 0}</span><span className="label">lessons</span></span>
									<FiChevronDown style={{ 
										transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
										transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
										color: isOpen ? '#0891b2' : 'inherit'
									}} />
								</span>
							</div>
						{isOpen && (
							<div className="module-content">
								<ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
									{m.lessons?.map((l, i) => (
										<li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
											<FiPlay /> 
											<span>{typeof l === 'string' ? l : l.title}</span>
											{l.duration && <span style={{ marginLeft: 'auto', color: '#666', fontSize: '0.9em' }}>{l.duration}</span>}
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				);
			}) : <p>Curriculum details coming soon...</p>}
		</CurriculumList>
	);

	const renderTabContent = () => {
		switch (activeTab) {
			case 'overview': return renderOverview();
			case 'courseinfo': return renderCourseInfo();
			case 'curriculum': return renderCurriculum();
			default: return null;
		}
	};

	// Function to render stars based on rating
	const renderStars = (rating) => {
		const stars = [];
		const fullStars = Math.floor(rating || 0);
		
		for (let i = 0; i < 5; i++) {
			if (i < fullStars) {
				stars.push(<FiStar key={i} className="filled" />);
			} else {
				stars.push(<FiStar key={i} className="empty" />);
			}
		}
		return stars;
	};

	return (
		<Page>
			<Hero>
				<Container>
					<BackLink to="/courses"><FiArrowLeft /> Back to Courses</BackLink>
					<HeroGrid>
						<HeroContent>
							{(course.rating || course.reviewsCount) && (
								<RatingRow>
									<span className="stars">{renderStars(course.rating)}</span>
									<span>{course.rating}{typeof course.reviewsCount !== 'undefined' ? ` (${course.reviewsCount} reviews)` : ''}</span>
								</RatingRow>
							)}
							<h1>{course.title}</h1>
							<p className="subtitle">{course.shortDescription}</p>
							{course.highlights?.length > 0 && (
								<Highlights>
									{course.highlights.slice(0, 6).map((h, i) => (
										<li key={i}><FiCheck /> {h}</li>
									))}
								</Highlights>
							)}
							<CTAGroup>
								{whatsappUrl ? (
									<PrimaryCTA as="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer">Enroll Now</PrimaryCTA>
								) : (
									<PrimaryCTA as={Link} to="/contact">Enroll Now</PrimaryCTA>
								)}
								<OutlineCTA onClick={goToCurriculum}>See Curriculum</OutlineCTA>
							</CTAGroup>
						</HeroContent>
						<HeroImageBox>
							<HeroImage src={course.image || course.thumbnail || `https://via.placeholder.com/800x420?text=${encodeURIComponent(course.title || 'Course')}`} alt={course.title} />
						</HeroImageBox>
					</HeroGrid>
					<TabsWrap>
						<TabList>
							{tabs.map(t => (
								<Tab key={t.id} active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
									{t.icon && <t.icon style={{ marginRight: 6 }} />} {t.label} {t.count ? <span style={{ marginLeft: 6, fontWeight: 800 }}>({t.count})</span> : null}
								</Tab>
							))}
						</TabList>
					</TabsWrap>
				</Container>
			</Hero>

			<ContentSection id="course-content">
				<Container>
					{renderTabContent()}
				</Container>
			</ContentSection>
		</Page>
	);
}

export default CourseDetail;
